import React, { useCallback, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { ThemeContext } from 'styled-components'
import { Spinner } from '@8x8/oxygen-loaders'
import CopyIcon from '@8x8/oxygen-icon/novo/CopyIcon'
import { font, spacing } from '@8x8/oxygen-constants'

import { copySharingLink, removeSharingLink, shareMeetingResources } from '~/actions/recents'
import { translate } from '~/core/translate'
import Modal from '~/components/common/modal'
import Button from '~/components/common/button'
import { getSharingUpdateInProgress } from '~/selectors/recents'
import { getSessionSharer, getSharingLink } from '~/selectors/session-details'
import { getDialogProps } from '~/selectors/ui'
import TextLink from '@8x8/oxygen-text-link'

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`

const Description = styled.div`
  margin-bottom: ${spacing.sm};
`

const CreateLinkButton = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colorLink};
  cursor: pointer;
  display: flex;
  font-weight: bold;
  margin: ${spacing.lg} 0 ${spacing.xs} 0;
`

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
`

const Owner = styled.div`
  color: ${({ theme }) => theme.colorSecondary};
  font-size: ${font.sizeXs};
  margin-bottom: ${spacing.lg};
  text-align: right;
`

const PlusIcon = styled.div`
  font-weight: 200;
  font-size: ${font.lineHeightLg};
  margin-right: ${spacing.xxs};
  padding-bottom: ${spacing.xxs};
`

const SharingLinkGroup = styled.div`
  cursor: pointer;
  display: flex;
  margin: ${spacing.md} ${spacing.xxl} ${spacing.md} 0;
`

const StyledCopyIcon = styled(CopyIcon)`
  align-self: center;
  display: flex;
  width: 100px;
  height: 20px;
  margin-right: ${spacing.xs};
`

const SharingLink = styled(TextLink)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledButton = styled(Button)`
  margin-left: ${spacing.md};
`

const ShareMeetingDialog = () => {
  const dispatch = useDispatch()
  const { id } = useSelector(getDialogProps)
  const sharingLink = useSelector(getSharingLink(id))
  const sharedBy = useSelector(getSessionSharer(id))
  const isLoading = useSelector(getSharingUpdateInProgress)
  const theme = useContext(ThemeContext)

  const handleCreateLink = useCallback(() => {
    dispatch(shareMeetingResources(id))
  }, [])

  const handleCopyLink = useCallback(() => {
    dispatch(copySharingLink(sharingLink))
  }, [sharingLink])

  const handleRemoveLink = useCallback(() => {
    dispatch(removeSharingLink(id))
  }, [id])

  const renderContent = useCallback(() => {
    if (isLoading) {
      return (
        <LoaderContainer>
          <Spinner hasAnimation theme={{ backgroundFiller: theme.colorSecondary }} />
        </LoaderContainer>
      )
    }

    if (sharingLink) {
      return (
        <>
          <SharingLinkGroup>
            <StyledCopyIcon onClick={handleCopyLink} size={20} />
            <SharingLink href={sharingLink} target='_blank'>{sharingLink}</SharingLink>
          </SharingLinkGroup>
          <Owner>{translate('shared_by_user', { user: sharedBy })}</Owner>
          <Actions>
            <StyledButton variant='secondary' onClick={handleRemoveLink}>
              Remove link
            </StyledButton>
            <StyledButton variant='primary' onClick={handleCopyLink}>
              Copy link
            </StyledButton>
          </Actions>
        </>
      )
    }

    return (
      <>
        <Description>{translate('create_sharing_link_description')}</Description>
        <CreateLinkButton onClick={handleCreateLink}>
          <PlusIcon>+</PlusIcon>
          <div>{translate('create_sharing_link')}</div>
        </CreateLinkButton>
      </>
    )
  }, [isLoading, sharingLink])

  return (
    <Modal
      title={translate('share_meeting')}
    >
      {renderContent()}
    </Modal>
  )
}

export default ShareMeetingDialog
